import React from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { LookupKeyEnum } from '../../../../defs/api';
import {
    BillingPeriod,
    getAvailablePremiumLookupKey,
    getBillingPeriodByLookupKey,
    getPremiumLookupKeyByBillingPeriod,
} from '../../../../models/functions/billing-period';
import { useAvailableBillingPeriods } from '../../../../providers/pricing-offer-provider';
import { useRootStore } from '../../../../providers/root-store-provider';
import { cn } from '../../../../utils/tailwind';
import { prices } from '../../../../blocks/web/pricing/pricing-data';

import { PremiumPricingCardContent } from '../../../../blocks/web/pricing/premium-pricing-card-content';
import pricingStyles from '../../../../blocks/web/pricing/homepage-pricing.module.css';
import { IconArrowDown } from '../../../app/icons/arrow-down';

type SignupSelectedPlanBannerProps = {
    lookupKey: LookupKeyEnum;
};

const getAllowedPeriod = (lookupKey: LookupKeyEnum, availablePeriods: BillingPeriod[]): BillingPeriod | null => {
    if (availablePeriods.length === 0) return null;

    const allowedLookupKey = getAvailablePremiumLookupKey(
        lookupKey,
        availablePeriods.map(getPremiumLookupKeyByBillingPeriod),
    );
    return getBillingPeriodByLookupKey(allowedLookupKey);
};

export const SignupSelectedPlanBanner: React.FC<SignupSelectedPlanBannerProps> = ({ lookupKey }) => {
    const { t } = useTranslation();
    const reducedMotion = !!useReducedMotion();
    const [expanded, setExpanded] = React.useState(false);
    const [withVat, setWithVat] = React.useState(true);
    const [reaction, setReaction] = React.useState<{ period: 'halfyearly' | 'yearly'; id: number } | null>(null);
    const reactionId = React.useRef(0);
    const { cookieService } = useRootStore();
    const availablePeriods = useAvailableBillingPeriods();
    const availablePeriodsKey = availablePeriods.join(',');
    const [selectedPeriod, setSelectedPeriod] = React.useState<BillingPeriod | null>(() =>
        getAllowedPeriod(lookupKey, availablePeriods),
    );

    React.useEffect(() => {
        setSelectedPeriod(getAllowedPeriod(lookupKey, availablePeriods));
    }, [lookupKey, availablePeriodsKey]);

    React.useEffect(() => {
        if (!reaction) return undefined;
        const timer = window.setTimeout(() => setReaction(null), 1600);
        return () => window.clearTimeout(timer);
    }, [reaction]);

    if (!selectedPeriod || !availablePeriods.includes(selectedPeriod)) {
        return null;
    }

    const selectPeriod = (period: BillingPeriod) => {
        if (!availablePeriods.includes(period)) return;

        const selectedLookupKey = getPremiumLookupKeyByBillingPeriod(period);
        cookieService.selectPreferredSubscription(selectedLookupKey);
        cookieService.setInterestedInSubscription(selectedLookupKey);
        setSelectedPeriod(period);
        if (period === selectedPeriod || period === 'monthly' || reducedMotion) {
            setReaction(null);
        } else {
            reactionId.current += 1;
            setReaction({ period, id: reactionId.current });
        }
    };

    const closeDetails = () => {
        setExpanded(false);
        setReaction(null);
    };

    return (
        <div className="contents">
            <button
                type="button"
                aria-expanded={expanded}
                aria-controls="signup-selected-plan-options"
                aria-label={t('dialogs.signUp.selectedPlan.edit')}
                onClick={() => {
                    if (expanded) closeDetails();
                    else setExpanded(true);
                }}
                className="ml-auto inline-flex min-h-[4.4rem] max-w-full items-center gap-[1.6rem] whitespace-nowrap rounded-full border-0 bg-[#d9cdc5]/20 px-[1.6rem] py-[0.6rem] text-left text-[1.4rem] leading-tight text-brand-secondary transition-colors hover:bg-[#d9cdc5]/40 md:mr-[4.8rem] focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-primary"
            >
                <span className="inline-flex items-baseline gap-[0.6rem]" aria-live="polite" aria-atomic="true">
                    <span className="font-bold">Premium</span>
                    <span aria-hidden="true" className="text-web-copy">
                        ·
                    </span>
                    <span className="text-web-copy">
                        {t('dialogs.signUp.selectedPlan.price', {
                            price: prices[selectedPeriod].withVat,
                            period: t(`dialogs.signUp.selectedPlan.period.${selectedPeriod}`),
                        })}
                    </span>
                </span>
                <span
                    aria-hidden="true"
                    className={cn(
                        'flex shrink-0 items-center justify-center text-[#9b8d82] transition-transform duration-200 motion-reduce:transition-none [&_i]:h-[1.2rem] [&_i]:w-[1.2rem]',
                        expanded && 'rotate-180',
                    )}
                >
                    <IconArrowDown />
                </span>
            </button>

            <AnimatePresence>
                {expanded && (
                    <motion.section
                        key="selected-plan-options"
                        id="signup-selected-plan-options"
                        aria-label={t('dialogs.signUp.selectedPlan.edit')}
                        initial={reducedMotion ? false : { height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: reducedMotion ? 0 : 0.3, ease: [0.22, 1, 0.36, 1] }}
                        className="min-w-0 basis-full overflow-visible"
                    >
                        <div className="pt-[2.8rem]">
                            <div
                                className={cn(
                                    pricingStyles.card,
                                    'min-w-0 overflow-visible rounded-[2.4rem] border border-[#d9cdc5] p-[2.4rem] sm:p-[3.2rem]',
                                )}
                            >
                                <PremiumPricingCardContent
                                    available={availablePeriods}
                                    period={selectedPeriod}
                                    withVat={withVat}
                                    onVatChange={setWithVat}
                                    onPeriodChange={selectPeriod}
                                    reaction={reaction}
                                    termsInTooltip
                                />
                            </div>
                        </div>
                    </motion.section>
                )}
            </AnimatePresence>
        </div>
    );
};
